import { useState, Dispatch, SetStateAction } from 'react';
import moment from 'moment-timezone';
import _ from 'lodash';
import { GqlCollection, GqlScheduleInput } from 'types/graphql';
import { TIME_FORMAT, getTimesWith420 } from 'src/utils/time-utils';
import useUI from '../../../shared/hooks/use-ui';
import { createScheduleTemplate, Day } from './utils';

type UseScheduleFormParams = {
  collection?: GqlCollection;
  currentStepIndex: number;
  setStepNumber: (newStep: number) => void;
  setCollectionData: (data: Partial<GqlCollection> & { schedule?: GqlScheduleInput | null }) => void;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
};

type UseScheduleFormReturn = {
  // State
  startDate: moment.Moment;
  endDate: moment.Moment;
  startTime: string;
  endTime: string;
  noEndDate: boolean;
  days: Day[];
  startDayVisible: boolean;
  endDayVisible: boolean;
  times: string[];
  dispensaryTimezone: string;

  // Setters
  setNoEndDate: Dispatch<SetStateAction<boolean>>;
  setStartDayVisible: Dispatch<SetStateAction<boolean>>;
  setEndDayVisible: Dispatch<SetStateAction<boolean>>;

  // Handlers
  handleUpdateDay: (updatedDay: Day) => void;
  handleUpdateStartTime: (time: string) => void;
  handleUpdateEndTime: (time: string) => void;
  handleUpdateStartDate: (date: moment.Moment) => void;
  handleUpdateEndDate: (date: moment.Moment) => void;
  handleCopyScheduleToAll: (day: Day) => void;
  handleToggleAllDay: (allDay: boolean, day: Day) => void;
  validateData: () => void;
};

/**
 * Custom hook to manage schedule form state and business logic
 */
export function useScheduleForm({
  collection,
  currentStepIndex,
  setStepNumber,
  setCollectionData,
  setIsDirty,
}: UseScheduleFormParams): UseScheduleFormReturn {
  const existingSchedule = collection?.schedule;
  const UI = useUI();
  const dispensaryTimezone = UI.dispensary?.timezone || moment.tz.guess();

  const [startDayVisible, setStartDayVisible] = useState(false);
  const [endDayVisible, setEndDayVisible] = useState(false);

  // Parse incoming UTC dates and convert to dispensary timezone for display
  const [startDate, setStartDate] = useState(() => {
    if (existingSchedule?.startDate) {
      return moment.tz(existingSchedule.startDate, dispensaryTimezone);
    }
    // Default to today at midnight (12:00 AM)
    return moment.tz(dispensaryTimezone).startOf('day');
  });

  const [endDate, setEndDate] = useState(() => {
    if (existingSchedule?.endDate) {
      return moment.tz(existingSchedule.endDate, dispensaryTimezone);
    }
    // Default to tomorrow at midnight (12:00 AM)
    return moment.tz(dispensaryTimezone).startOf('day').add(1, 'day');
  });

  const [startTime, setStartTime] = useState(() => {
    if (existingSchedule?.startDate) {
      return moment.tz(existingSchedule.startDate, dispensaryTimezone).format(TIME_FORMAT);
    }
    // Default to midnight (12:00 AM)
    return moment.tz(dispensaryTimezone).startOf('day').format(TIME_FORMAT);
  });

  const [endTime, setEndTime] = useState(() => {
    if (existingSchedule?.endDate) {
      return moment.tz(existingSchedule.endDate, dispensaryTimezone).format(TIME_FORMAT);
    }
    // Default to midnight (12:00 AM)
    return moment.tz(dispensaryTimezone).startOf('day').format(TIME_FORMAT);
  });

  const [noEndDate, setNoEndDate] = useState(!existingSchedule?.endDate);
  const [days, setDays] = useState(createScheduleTemplate(existingSchedule?.timeRanges ?? null));

  const handleUpdateDay = (updatedDay: Day): void => {
    setDays(days.map((d) => (d.key === updatedDay.key ? updatedDay : d)));
  };

  const handleUpdateStartTime = (time: string): void => {
    setStartTime(time);
    const momentTime = moment(time, 'h:mm a');
    // startDate is already in dispensary timezone, just update the time portion
    const newStartDate = startDate.clone().set('hour', momentTime.get('hour')).set('minute', momentTime.get('minute'));
    setStartDate(newStartDate);
  };

  const handleUpdateEndTime = (time: string): void => {
    setEndTime(time);
    const momentTime = moment(time, 'h:mm a');
    // endDate is already in dispensary timezone, just update the time portion
    const newEndDate = endDate.clone().set('hour', momentTime.get('hour')).set('minute', momentTime.get('minute'));
    setEndDate(newEndDate);
  };

  const handleUpdateStartDate = (date: moment.Moment): void => {
    setStartDate(date);
    if (date.isAfter(endDate, 'day')) {
      setEndDate(date.clone().add(1, 'day'));
      if (!noEndDate) {
        UI.showErnie('You must select an end date that is after the start date.', 'danger');
      }
    }
  };

  const handleUpdateEndDate = (date: moment.Moment): void => {
    setEndDate(date);
    if (date.isBefore(startDate, 'day')) {
      UI.showErnie('You must select an end date that is after the start date.', 'danger');
      setStartDate(date.clone().startOf('day'));
      return;
    }
    // if end date is the same as start date, set the end date to the next day
    if (date.isSame(startDate, 'day')) {
      UI.showErnie('You cannot select the same start and end date.', 'danger');
      setEndDate(date.clone().add(1, 'day'));
    }
  };

  const handleCopyScheduleToAll = (day: Day): void => {
    setDays(
      days.map((d) => ({
        ...d,
        startTime: day.startTime,
        endTime: day.endTime,
        allDay: day.allDay,
        enabled: day.enabled,
      }))
    );
  };

  const handleToggleAllDay = (allDay: boolean, day: Day): void => {
    if (allDay) {
      handleUpdateDay({
        ...day,
        startTime: moment.tz(dispensaryTimezone).startOf('day').format(TIME_FORMAT),
        endTime: moment.tz(dispensaryTimezone).endOf('day').format(TIME_FORMAT),
        allDay: true,
      });
    } else {
      handleUpdateDay({
        ...day,
        startTime: moment('9:00 AM', TIME_FORMAT).format(TIME_FORMAT),
        endTime: moment('5:00 PM', TIME_FORMAT).format(TIME_FORMAT),
        allDay: false,
      });
    }
  };

  const validateData = (): void => {
    if (!noEndDate && endDate.isBefore(startDate)) {
      UI.showErnie('You must select an end date that is after the start date.', 'danger');
      return;
    }

    if (!noEndDate && moment(startDate, TIME_FORMAT).isAfter(moment(endDate, TIME_FORMAT))) {
      UI.showErnie('You must select an end time that is after the start time.', 'danger');
      return;
    }

    // Convert dispensary timezone dates to UTC for server submission
    // startDate and endDate are already moment objects in dispensary timezone
    const startDateUTC = startDate.clone().utc().toISOString();
    const endDateUTC = noEndDate ? null : endDate.clone().utc().toISOString();

    const schedule: GqlScheduleInput = {
      id: existingSchedule?.id,
      startDate: startDateUTC,
      endDate: noEndDate ? null : endDateUTC,
      timeRanges: days
        .filter((day) => day.enabled)
        .map((day) => ({
          dayOfWeek: day.key,
          startTime: day.allDay ? moment.tz(dispensaryTimezone).startOf('day').format('HH:mm') : day.startTime,
          endTime: day.allDay ? moment.tz(dispensaryTimezone).endOf('day').format('HH:mm') : day.endTime,
        }))
        .sort((a, b) => a.dayOfWeek - b.dayOfWeek),
    };

    const originalSchedule: GqlScheduleInput | null = existingSchedule
      ? {
          id: existingSchedule.id,
          startDate: existingSchedule.startDate,
          endDate: existingSchedule.endDate,
          timeRanges: existingSchedule.timeRanges
            ?.map((tr) => ({
              dayOfWeek: tr.dayOfWeek,
              startTime: tr.startTime,
              endTime: tr.endTime,
            }))
            .sort((a, b) => a.dayOfWeek - b.dayOfWeek),
        }
      : null;

    // if the timeRanges array is empty, show an Ernie and return;
    if (schedule.timeRanges?.length === 0) {
      UI.showErnie('You must select at least one day and time for the schedule.', 'danger');
      return;
    }

    if (!_.isEqual(schedule, originalSchedule)) {
      const updatedCollection = {
        ...collection,
        schedule,
      } as Partial<GqlCollection> & { schedule: GqlScheduleInput };

      setCollectionData(updatedCollection);
      setIsDirty(true);
    }

    setStepNumber(currentStepIndex + 2);
  };

  // Generate time options in the dispensary timezone
  const nowStart = moment.tz(dispensaryTimezone).startOf('day').add(-15, 'minute');
  const times = getTimesWith420(nowStart);

  return {
    // State
    startDate,
    endDate,
    startTime,
    endTime,
    noEndDate,
    days,
    startDayVisible,
    endDayVisible,
    times,
    dispensaryTimezone,
    // Setters
    setNoEndDate,
    setStartDayVisible,
    setEndDayVisible,

    // Handlers
    handleUpdateDay,
    handleUpdateStartTime,
    handleUpdateEndTime,
    handleUpdateStartDate,
    handleUpdateEndDate,
    handleCopyScheduleToAll,
    handleToggleAllDay,
    validateData,
  };
}
